import { ReactNode, useState } from 'react';
import { Control, Controller, FieldErrors, FieldPath, FieldValues, get } from 'react-hook-form';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { classNames } from 'primereact/utils';
import styles from './EditableField.module.css';

export type EditableFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  field: TName;
  display: ReactNode;
  label?: string;
  placeholder?: string;
  onSave?: (value: any) => void;
  disabled?: boolean;
  className?: string;
};

const EditableField = <TFieldValues extends FieldValues>({
  control,
  errors,
  field,
  display,
  label,
  placeholder,
  onSave,
  disabled,
  className,
}: EditableFieldProps<TFieldValues>): JSX.Element => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState<any>(null);

  const handleEdit = (currentValue: any) => {
    setTempValue(currentValue);
    setIsEditing(true);
  };

  const handleSave = (onChange: (value: any) => void) => {
    onChange(tempValue);
    setIsEditing(false);
    if (onSave) {
      onSave(tempValue);
    }
  };

  const handleCancel = () => {
    setTempValue(null);
    setIsEditing(false);
  };

  const errorTemplate = () => {
    const error = get(errors, field);
    return error && <small className="p-error">{error?.message as string}</small>;
  };

  return (
    <div className={className}>
      <Controller
        name={field}
        control={control}
        render={({ field: fieldProps, fieldState }) => (
          <>
            {!isEditing ? (
              // Display Mode
              <div className={`flex items-center gap-2 ${styles.displayMode}`}>
                <div className="flex-1 min-w-0">{display}</div>
                {!disabled && (
                  <Button
                    type="button"
                    icon="pi pi-pencil"
                    className={classNames('p-button-text p-button-sm', styles.editButton)}
                    onClick={() => handleEdit(fieldProps.value)}
                    title="Edit"
                  />
                )}
              </div>
            ) : (
              // Edit Mode
              <div className="flex flex-col gap-2">
                {label && (
                  <label
                    htmlFor={fieldProps.name}
                    className={classNames('block mb-1 font-medium', { 'p-error': fieldState.error })}
                  >
                    {label}
                  </label>
                )}
                <div className="flex gap-2 items-start">
                  <InputText
                    id={fieldProps.name}
                    value={tempValue ?? ''}
                    onChange={(e) => setTempValue(e.target.value || null)}
                    className={classNames('flex-1', { 'p-invalid': fieldState.error })}
                    placeholder={placeholder}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSave(fieldProps.onChange);
                      } else if (e.key === 'Escape') {
                        handleCancel();
                      }
                    }}
                  />
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      icon="pi pi-check"
                      className="p-button-success p-button-sm"
                      onClick={() => handleSave(fieldProps.onChange)}
                      title="Save"
                    />
                    <Button
                      type="button"
                      icon="pi pi-times"
                      className="p-button-text p-button-sm"
                      onClick={handleCancel}
                      title="Cancel"
                    />
                  </div>
                </div>
                {errorTemplate()}
              </div>
            )}
          </>
        )}
      />
    </div>
  );
};

export default EditableField;
